import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatINR } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import RequireCompany from "@/components/RequireCompany";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Printer } from "lucide-react";
import { devError } from "@/lib/logger";

export default function Payslips() {
  return <RequireCompany><Inner /></RequireCompany>;
}

function Inner() {
  const { activeId } = useCompany();
  const [search, setSearch] = useSearchParams();
  const [month, setMonth] = useState(() => {
    const fromUrl = search.get("month");
    if (fromUrl) return fromUrl;
    const d = new Date(Date.now() - 30 * 24 * 3600 * 1000);
    return d.toISOString().slice(0, 7);
  });
  const [slips, setSlips] = useState([]);

  useEffect(() => {
    if (!activeId) return;
    let mounted = true;
    api.get(`/companies/${activeId}/payslips`, { params: { month } })
      .then((r) => { if (mounted) setSlips(r.data); })
      .catch((err) => devError("Payslip list failed", err));
    return () => { mounted = false; };
  }, [activeId, month]);

  const onMonth = (v) => { setMonth(v); setSearch({ month: v }); };

  const totals = slips.reduce((acc, s) => ({
    gross: acc.gross + s.gross,
    deductions: acc.deductions + s.total_deductions,
    net: acc.net + s.net_pay,
  }), { gross: 0, deductions: 0, net: 0 });

  return (
    <>
      <PageHeader title="Payslips" subtitle={`${slips.length} payslip${slips.length !== 1 ? "s" : ""}`} />
      <div className="p-6 space-y-4" data-testid="payslips-page">
        <div className="flex items-end gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-gray-500">Month</Label>
            <Input type="month" value={month} onChange={(e) => onMonth(e.target.value)} className="h-9 w-44" data-testid="payslip-month" />
          </div>
          <div className="ml-auto grid grid-cols-3 gap-3 text-right" data-testid="payslip-totals">
            <div className="border border-gray-200 rounded bg-white px-3 py-2"><div className="text-[10px] uppercase text-gray-500">Total Gross</div><div className="font-mono text-sm font-semibold text-blue-700">₹ {formatINR(totals.gross)}</div></div>
            <div className="border border-gray-200 rounded bg-white px-3 py-2"><div className="text-[10px] uppercase text-gray-500">Deductions</div><div className="font-mono text-sm font-semibold text-orange-700">₹ {formatINR(totals.deductions)}</div></div>
            <div className="border border-gray-200 rounded bg-white px-3 py-2"><div className="text-[10px] uppercase text-gray-500">Net Pay</div><div className="font-mono text-sm font-semibold text-emerald-700">₹ {formatINR(totals.net)}</div></div>
          </div>
        </div>

        <div className="border border-gray-200 rounded bg-white overflow-hidden">
          <table className="tx-table">
            <thead>
              <tr>
                <th>Code</th><th>Name</th><th>Designation</th>
                <th className="text-right">Gross</th><th className="text-right">PF</th><th className="text-right">ESI</th>
                <th className="text-right">PT</th><th className="text-right">TDS</th>
                <th className="text-right">Net Pay</th>
                <th></th>
              </tr>
            </thead>
            <tbody data-testid="payslips-rows">
              {slips.length === 0 ? (
                <tr><td colSpan={10} className="text-center text-xs text-gray-400 py-6">No payslips for this month. Run a pay run first.</td></tr>
              ) : slips.map((s) => (
                <tr key={s.id} data-testid={`payslip-row-${s.employee_code}`}>
                  <td className="font-mono text-xs">{s.employee_code}</td>
                  <td className="text-xs font-semibold">{s.employee_name}</td>
                  <td className="text-xs">{s.designation || "—"}</td>
                  <td className="tx-num">{formatINR(s.gross)}</td>
                  <td className="tx-num">{formatINR(s.deductions.pf)}</td>
                  <td className="tx-num">{formatINR(s.deductions.esi)}</td>
                  <td className="tx-num">{formatINR(s.deductions.pt)}</td>
                  <td className="tx-num">{formatINR(s.deductions.tds)}</td>
                  <td className="tx-num font-semibold text-emerald-700">{formatINR(s.net_pay)}</td>
                  <td className="text-right">
                    <Link to={`/payroll/payslips/${s.id}/print`}>
                      <Button size="sm" variant="outline" className="h-7 px-2 text-xs" data-testid={`btn-view-payslip-${s.employee_code}`}><Printer className="w-3 h-3 mr-1" /> View</Button>
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
